import styled from 'styled-components'
import { createContext, FC, useCallback, useContext, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Modal } from 'Components/Modal/Modal'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ModalHeader } from 'Components/Modal/ModalHeader'

import {
  ConfirmationModalContent,
  ConfirmationModalContextProviderProps,
  useModalShow
} from 'Hooks/modals/useModalShow'

import { spacingSM } from 'Utils/styles/spacing'
import { ThinDarkGreenText } from 'Utils/styles/text'
import { FormDatePicker } from 'Components/Inputs/Calendar/FormDatePicker'
import { createDate } from 'Utils/helpers/dateUtils'
import { FormTextInput } from 'Components/Inputs/FormTextInput'
import { CountryCode, CountryNames } from 'Utils/types/countries'
import { StyledSelect } from 'Components/Inputs/StyledSelect'

const ButtonContainer = styled.div`
  display: flex;
  gap: ${spacingSM};
  justify-content: end;
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingSM};
  margin: ${spacingSM} 0;
`

const DateRow = styled.div`
  display: flex;
  gap: ${spacingSM};
  align-items: flex-start;
`

const PresetAlign = styled.div`
  align-self: flex-end;
`

const YtdButton = styled(StyledButton)`
  height: 50px;
`

const DateLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const LabelText = styled(ThinDarkGreenText)`
  font-weight: 500;
`

export type ConsolidatedReportFormData = {
  startDate: Date
  endDate: Date
  reportName: string
  countryCode: string
}

type FormValues = {
  reportName: string
}

interface ConsolidatedReportModalOptions {
  countries: CountryCode[]
}

interface ModalContextType {
  showModal: (
    options: ConsolidatedReportModalOptions
  ) => Promise<ConsolidatedReportFormData | undefined>
}

const UseConsolidatedPurchasesReportModal = createContext<ModalContextType>({} as ModalContextType)

const ConsolidatedPurchasesReportModalProvider: FC<ConfirmationModalContextProviderProps> = (
  props
) => {
  const { setShow, show, onHide } = useModalShow()

  const [startDate, setStartDate] = useState(createDate())
  const [endDate, setEndDate] = useState(createDate())
  const [selectedCountry, setSelectedCountry] = useState<string>('')
  const [availableCountries, setAvailableCountries] = useState<CountryCode[]>([])
  const [content, setContent] = useState<ConfirmationModalContent | null>()
  const resolver =
    useRef<
      (
        value:
          | ConsolidatedReportFormData
          | undefined
          | PromiseLike<ConsolidatedReportFormData | undefined>
      ) => void
    >()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid }
  } = useForm<FormValues>()

  const applyYearToDatePreset = useCallback(() => {
    const today = createDate()
    const previousDecember = new Date(today.getFullYear() - 1, 11, 1)
    setStartDate(previousDecember)
    setEndDate(today)
    setValue('reportName', `YTD ${today.getFullYear()}`, { shouldValidate: true })
  }, [setValue])

  const showModal = (
    options: ConsolidatedReportModalOptions
  ): Promise<ConsolidatedReportFormData | undefined> => {
    const { countries } = options

    setContent({
      title: 'Consolidated Purchases Report',
      message:
        'Select date range, country, and provide report name. The report will be generated in Google Drive, and a link will be sent to #reporting on Slack.',
      confirmButtonId: 'continue.label',
      cancelButtonId: 'cancel.label'
    })

    setAvailableCountries(countries)
    setSelectedCountry(countries[0] || 'US')

    setShow(true)
    return new Promise((resolve) => {
      resolver.current = resolve
    })
  }

  const handleOk = useCallback(
    (data: FormValues) => {
      if (resolver.current) {
        resolver.current({
          startDate,
          endDate,
          reportName: data.reportName,
          countryCode: selectedCountry
        })
      }
      reset()
      onHide()
    },
    [startDate, endDate, selectedCountry, reset, onHide]
  )

  const onConfirmClick = useCallback(async () => {
    await handleSubmit(handleOk)()
  }, [handleSubmit, handleOk])

  const handleCancel = () => {
    if (resolver.current) {
      resolver.current(undefined)
    }
    reset()
    onHide()
  }

  return (
    <UseConsolidatedPurchasesReportModal.Provider
      value={{
        showModal
      }}
    >
      {props.children}

      {content && (
        <Modal showModal={show} setShowModal={onHide}>
          <ModalHeader>{content.title}</ModalHeader>

          <ThinDarkGreenText>{content.message}</ThinDarkGreenText>

          <FormContainer>
            <DateRow>
              <DateLabel>
                <LabelText>Start Date</LabelText>
                <FormDatePicker date={startDate} onChange={setStartDate} disabled={false} />
              </DateLabel>
              <DateLabel>
                <LabelText>End Date</LabelText>
                <FormDatePicker date={endDate} onChange={setEndDate} disabled={false} />
              </DateLabel>
              <PresetAlign>
                <YtdButton
                  id="purchaseReports.ytdPreset.label"
                  secondary
                  onClick={applyYearToDatePreset}
                />
              </PresetAlign>
            </DateRow>

            <FormTextInput
              fieldName="reportName"
              register={register}
              labelId="reportName.label"
              registerOptions={{ required: true }}
              placeholder="e.g., Q1 2024"
            />

            <StyledSelect
              options={availableCountries.map((countryCode) => ({
                label: CountryNames[countryCode],
                value: countryCode
              }))}
              handleChange={setSelectedCountry}
              placeholderText="Select a country"
              defaultValue={selectedCountry}
            />
          </FormContainer>

          <ButtonContainer>
            <StyledButton id={content.cancelButtonId} onClick={handleCancel} />
            <StyledButton
              id={content.confirmButtonId}
              primary
              onClick={onConfirmClick}
              disabled={!isValid}
            />
          </ButtonContainer>
        </Modal>
      )}
    </UseConsolidatedPurchasesReportModal.Provider>
  )
}

const useConsolidatedPurchasesReportModal = (): ModalContextType =>
  useContext(UseConsolidatedPurchasesReportModal)

export { useConsolidatedPurchasesReportModal }

export default ConsolidatedPurchasesReportModalProvider
